import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import RisposteInviate from '../components/survey/RisposteInviate';
import { useRisposteInviate } from '../hooks/useRisposteInviate';
import { percorsiArea } from '../services/surveyAuth';

/**
 * Area riservata: risposte inviate a un sondaggio completato, in sola lettura
 * (dalla sezione «Completati» di /area-riservata/sondaggi).
 */

const percorsi = percorsiArea('riservata');

const SurveyAnswersPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { stato, survey, response, messaggio } = useRisposteInviate(id, 'riservata');

  let contenuto: React.ReactNode;
  if (stato === 'caricamento') {
    contenuto = (
      <div className="flex justify-center py-16" aria-label="Caricamento">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  } else if (stato === 'ok' && survey && response) {
    contenuto = <RisposteInviate survey={survey} response={response} />;
  } else {
    const testo = stato === 'nessuna'
      ? 'Nessuna risposta inviata per questo sondaggio'
      : stato === 'sessione'
        ? 'La sessione è scaduta: accedi di nuovo all\'area riservata per vedere le risposte'
        : messaggio || 'Impossibile caricare le risposte. Riprova più tardi.';
    contenuto = (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
        <p
          role={stato === 'nessuna' ? undefined : 'alert'}
          className={`text-sm ${stato === 'nessuna' ? 'text-gray-500 dark:text-gray-400' : 'text-red-600 dark:text-red-400'}`}
        >
          {testo}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 shadow">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Breadcrumb items={[
            { label: 'Area Riservata', href: percorsi.home },
            { label: 'Sondaggi', href: percorsi.elenco },
            { label: 'Risposte inviate', current: true },
          ]} />
          <div className="mt-4">
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">Risposte inviate</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Le risposte che hai inviato, in sola lettura
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        {contenuto}
        <button
          type="button"
          onClick={() => navigate(percorsi.elenco)}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          Torna ai sondaggi
        </button>
      </div>
    </div>
  );
};

export default SurveyAnswersPage;
